/**
 * Plate Holder Configurator
 * Personalizes ONLY the black plate holder bar rendered by plate-holder-svg.liquid.
 * Never touches the official license plate — there is no such control surface here.
 *
 * Base silhouette ships in snippets/plate-holder-svg.liquid (rectangular holder
 * with bottom tab + mounting holes). This file morphs the bottom tab width and
 * safe-zone from the live character count (max 10), and shrinks font size as
 * more letters are typed.
 */
(function () {
  if (customElements.get('plate-holder-configurator')) return;

  var MATERIALS = {
    glossBlack: { label: 'Hoogglans zwart', fill: 'material-glossBlack', pattern: null, reflectionOpacity: 0.5, textColor: '#ffffff' },
    matteBlack: { label: 'Mat zwart', fill: 'material-matteBlack', pattern: null, reflectionOpacity: 0.1, textColor: '#ffffff' },
    carbon: { label: 'Carbonlook', fill: 'material-carbon-weave', pattern: 'material-carbon-sheen', reflectionOpacity: 0.18, textColor: '#ffffff' },
    mirrorBlack: { label: 'Spiegel zwart', fill: 'material-mirrorBlack', pattern: null, reflectionOpacity: 0.6, textColor: '#ffffff' },
    mirrorSilver: { label: 'Spiegel zilver', fill: 'material-mirrorSilver', pattern: null, reflectionOpacity: 0.6, textColor: '#101010' },
    mirrorGold: { label: 'Spiegel goud', fill: 'material-mirrorGold', pattern: null, reflectionOpacity: 0.55, textColor: '#101010' },
    brushedAluminium: { label: 'Brushed aluminium', fill: 'material-brushedAluminium', pattern: 'material-brushed-lines', reflectionOpacity: 0.22, textColor: '#101010' },
    metallicBlack: { label: 'Metallic zwart', fill: 'material-metallicBlack', pattern: null, reflectionOpacity: 0.32, textColor: '#ffffff' }
  };


  /* Material -> Shopify variant title prefix (owner decision 2026-10-26). */
  var MATERIAL_VARIANT_MATCH = { glossBlack: 'Glanzend zwart plexiglas', matteBlack: 'Mat zwart plexiglas', mirrorGold: 'Spiegel goud' };

  var SOCIAL_PLATFORMS = {
    instagram: { label: 'Instagram', symbol: 'social-instagram' },
    facebook: { label: 'Facebook', symbol: 'social-facebook' },
    x: { label: 'X', symbol: 'social-x' },
    tiktok: { label: 'TikTok', symbol: 'social-tiktok' },
    youtube: { label: 'YouTube', symbol: 'social-youtube' },
    linkedin: { label: 'LinkedIn', symbol: 'social-linkedin' },
    whatsapp: { label: 'WhatsApp', symbol: 'social-whatsapp' }
  };

  var PRESET_LOGOS = {
    star: { label: 'Ster', symbol: 'preset-logo-star' },
    shield: { label: 'Schild', symbol: 'preset-logo-shield' },
    diamond: { label: 'Ruit', symbol: 'preset-logo-diamond' }
  };

  var LOGO_SLOT_SIZE = 20; /* svg user units, matches the symbols' visual footprint */
  var LOGO_SLOT_GAP = 6;

  /* Raster-only whitelist: no SVG upload, so nothing here can carry an XSS
     payload through to the live preview or a rendered order asset. */
  var ALLOWED_LOGO_TYPES = ['image/png', 'image/jpeg', 'image/webp'];
  var MAX_LOGO_FILE_BYTES = 5 * 1024 * 1024;

  function sanitizeVisibleText(value) {
    var noTags = value.split('<').join('').split('>').join('');
    var chars = noTags.split('');
    var cleaned = chars.filter(function (ch) {
      var code = ch.charCodeAt(0);
      return code >= 32 && code !== 127;
    });
    return cleaned.join('');
  }

  function debounce(fn, wait) {
    var timeoutId = null;
    var delay = typeof wait === 'number' ? wait : 16;
    return function () {
      var args = arguments;
      var self = this;
      if (timeoutId) clearTimeout(timeoutId);
      timeoutId = setTimeout(function () {
        fn.apply(self, args);
      }, delay);
    };
  }

  class PlateHolderConfiguratorElement extends HTMLElement {
    constructor() {
      super();
      this.instanceId = this.dataset.instanceId || 'default';
      this.formId = this.dataset.formId || '';
      this.maxChars = parseInt(this.dataset.maxChars || '10', 10);
      this.minFontSize = parseFloat(this.dataset.minFontSize || '18');
      this.maxFontSize = parseFloat(this.dataset.maxFontSize || '64');
      this.warningText = this.dataset.warningText || 'Tekst past niet binnen de plaathouder. Kort de tekst in.';
      this.availableMaterials = (this.dataset.materials || Object.keys(MATERIALS).join(',')).split(',').map(function (s) { return s.trim(); }).filter(Boolean);
      this.availableFonts = (this.dataset.fonts || 'Arial Narrow,Arial,Helvetica,Georgia,Impact').split(',').map(function (s) { return s.trim(); }).filter(Boolean);
      this.availableColors = (this.dataset.colors || '#ffffff,#e2b23c,#c62828,#9aa0a6,#2e9e44').split(',').map(function (s) { return s.trim(); }).filter(Boolean);

      this.defaultText = this.dataset.defaultText || '';
      this.defaultMaterial = this.dataset.defaultMaterial || 'glossBlack';
      this.defaultFont = this.dataset.defaultFont || this.availableFonts[0];
      this.defaultColor = this.dataset.defaultColor || this.availableColors[0];

      this._textColorTouched = false;
      this._debouncedRenderText = debounce(this._renderText.bind(this), 0);

      this.svg = this.querySelector('[data-plate-holder-svg]');
      if (!this.svg) return; /* defensive: missing DOM should not throw */

      this.textEl = this.svg.querySelector('[data-custom-text]');
      this.materialFillEl = this.svg.querySelector('[data-material-fill]');
      this.materialPatternEl = this.svg.querySelector('[data-material-pattern]');
      this.reflectionEl = this.svg.querySelector('[data-reflection-fill]');
      this.leftLogoGroup = this.svg.querySelector('[data-left-logo]');
      this.rightLogoGroup = this.svg.querySelector('[data-right-logo]');
      this.logoImageEl = this.svg.querySelector('[data-custom-logo-image]');
      this.safeZoneEl = this.svg.querySelector('[data-safe-zone]');
      this.holderPathEls = this.svg.querySelectorAll('[data-holder-path]');
      this.topContourEl = this.svg.querySelector('[data-top-contour]');
      this.bottomContourEl = this.svg.querySelector('[data-bottom-contour]');
      this.tabShadeTopEl = this.svg.querySelector('[data-tab-shade-top]');
      this.tabShadeBotEl = this.svg.querySelector('[data-tab-shade-bot]');
      this.mountHoles = {
        tl: this.svg.querySelector('[data-mount-hole="tl"]'),
        tr: this.svg.querySelector('[data-mount-hole="tr"]'),
        bl: this.svg.querySelector('[data-mount-hole="bl"]'),
        br: this.svg.querySelector('[data-mount-hole="br"]')
      };
      this._logoObjectUrl = null;

      this.geometry = this._readGeometry();
      this.state = this._getDefaultState();

      this._cacheControls();
      this._bindControls();
      this.render();
    }

    /* ---------------------------------------------------------------- */
    /* Geometry (read once from the SVG itself — never duplicated here) */
    /* ---------------------------------------------------------------- */
    _readGeometry() {
      var vb = this.svg.viewBox && this.svg.viewBox.baseVal;
      var width = vb ? vb.width : parseFloat(this.svg.getAttribute('viewBox').split(' ')[2]);
      var height = vb ? vb.height : parseFloat(this.svg.getAttribute('viewBox').split(' ')[3]);
      return {
        width: width,
        height: height,
        safeZoneX: parseFloat(this.safeZoneEl.getAttribute('x')),
        safeZoneY: parseFloat(this.safeZoneEl.getAttribute('y')),
        safeZoneWidth: parseFloat(this.safeZoneEl.getAttribute('width')),
        safeZoneHeight: parseFloat(this.safeZoneEl.getAttribute('height'))
      };
    }


    /* ---------------------------------------------------------------- */
    /* Silhouette: rectangular holder + bottom tab that widens w/ text  */
    /* ---------------------------------------------------------------- */
    _flareFromCharCount(charCount) {
      var n = Math.max(0, Math.min(this.maxChars, charCount | 0));
      if (this.maxChars <= 1) return n > 0 ? 1 : 0;
      return Math.max(0, n - 1) / (this.maxChars - 1);
    }

    _buildSilhouette(charCount) {
      /* Rectangular holder + bottom tab. Tab width grows with character count
         so the letters can stay large and still sit fully on the black area. */
      var n = Math.max(1, Math.min(this.maxChars, charCount | 0));
      var t = this._flareFromCharCount(n);
      var left = 18;
      var right = 542;
      var top = 24;
      var bodyBot = 88;
      var tabBot = 138;
      var cx = (left + right) / 2;

      /* Generous tab: ~1–2 chars stay compact; 10 chars nearly full plate
         so font does not need to collapse to stay inside. */
      var tabHalf = 70 + t * 175; /* 70 → 245 (tab width 140 → 490) */
      var diagRun = 44 + (1 - t) * 14;
      var sideFlare = t * 8;

      var botLeft = left - sideFlare;
      var botRight = right + sideFlare;
      var tabLeft = cx - tabHalf;
      var tabRight = cx + tabHalf;
      /* Keep tab inside the plate wings */
      var maxHalf = (right - left) / 2 - 36;
      if (tabHalf > maxHalf) {
        tabHalf = maxHalf;
        tabLeft = cx - tabHalf;
        tabRight = cx + tabHalf;
      }
      var wingLeftEnd = Math.max(botLeft + 24, tabLeft - diagRun);
      var wingRightStart = Math.min(botRight - 24, tabRight + diagRun);

      function r(v) { return Math.round(v * 10) / 10; }

      var path = [
        'M' + r(left + 8) + ',' + top,
        'H' + r(right - 8),
        'Q' + right + ',' + top + ' ' + right + ',' + r(top + 8),
        'V' + r(bodyBot - 8),
        'Q' + right + ',' + bodyBot + ' ' + r(botRight - 8) + ',' + bodyBot,
        'H' + r(wingRightStart),
        'L' + r(tabRight) + ',' + r(tabBot - 4),
        'Q' + r(tabRight - 2) + ',' + tabBot + ' ' + r(tabRight - 8) + ',' + tabBot,
        'H' + r(tabLeft + 8),
        'Q' + r(tabLeft + 2) + ',' + tabBot + ' ' + r(tabLeft) + ',' + r(tabBot - 4),
        'L' + r(wingLeftEnd) + ',' + bodyBot,
        'H' + r(botLeft + 8),
        'Q' + botLeft + ',' + bodyBot + ' ' + left + ',' + r(bodyBot - 8),
        'V' + r(top + 8),
        'Q' + left + ',' + top + ' ' + r(left + 8) + ',' + top,
        'Z'
      ].join(' ');

      var topContour = 'M' + r(left + 8) + ',' + top + ' H' + r(right - 8);
      var bottomContour = [
        'M' + r(botLeft + 8) + ',' + bodyBot,
        'H' + r(wingLeftEnd),
        'L' + r(tabLeft) + ',' + r(tabBot - 4),
        'Q' + r(tabLeft + 2) + ',' + tabBot + ' ' + r(tabLeft + 8) + ',' + tabBot,
        'H' + r(tabRight - 8),
        'Q' + r(tabRight - 2) + ',' + tabBot + ' ' + r(tabRight) + ',' + r(tabBot - 4),
        'L' + r(wingRightStart) + ',' + bodyBot,
        'H' + r(botRight - 8)
      ].join(' ');

      /* Safe zone = inner rectangle of the flat tab bottom (not the diagonals).
         Small inset so glyphs stay clearly on the black plastic. */
      var safePadX = 14;
      var safePadTop = 8;
      var safePadBot = 8;
      var safeX = tabLeft + safePadX;
      var safeW = Math.max(48, (tabRight - tabLeft) - safePadX * 2);
      var safeY = bodyBot + safePadTop;
      var safeH = Math.max(28, tabBot - bodyBot - safePadTop - safePadBot);

      return {
        path: path,
        topContour: topContour,
        bottomContour: bottomContour,
        safeZoneX: safeX,
        safeZoneY: safeY,
        safeZoneWidth: safeW,
        safeZoneHeight: safeH,
        holes: {
          tl: { cx: left + 30, cy: top + 18 },
          tr: { cx: right - 30, cy: top + 18 },
          bl: { cx: left + 30, cy: bodyBot - 18 },
          br: { cx: right - 30, cy: bodyBot - 18 }
        },
        tabShade: {
          x: tabLeft,
          width: tabRight - tabLeft
        }
      };
    }

    _updateSilhouette(charCount) {
      /* Photo-based preview: keep the real plate/holder image fixed; only
         the custom text size/fit changes inside the tab safe-zone. */
      if (this.svg && this.svg.getAttribute('data-fixed-silhouette') === 'true') {
        return;
      }
      var geo = this._buildSilhouette(charCount);
      if (this.holderPathEls && this.holderPathEls.length) {
        for (var i = 0; i < this.holderPathEls.length; i++) {
          this.holderPathEls[i].setAttribute('d', geo.path);
        }
      }
      if (this.topContourEl) this.topContourEl.setAttribute('d', geo.topContour);
      if (this.bottomContourEl) this.bottomContourEl.setAttribute('d', geo.bottomContour);
      if (this.safeZoneEl) {
        this.safeZoneEl.setAttribute('x', geo.safeZoneX);
        this.safeZoneEl.setAttribute('y', geo.safeZoneY);
        this.safeZoneEl.setAttribute('width', geo.safeZoneWidth);
        this.safeZoneEl.setAttribute('height', geo.safeZoneHeight);
      }
      if (this.tabShadeTopEl) {
        this.tabShadeTopEl.setAttribute('x', geo.tabShade.x);
        this.tabShadeTopEl.setAttribute('width', geo.tabShade.width);
        this.tabShadeTopEl.setAttribute('y', 86);
      }
      if (this.tabShadeBotEl) {
        this.tabShadeBotEl.setAttribute('x', geo.tabShade.x);
        this.tabShadeBotEl.setAttribute('width', geo.tabShade.width);
        this.tabShadeBotEl.setAttribute('y', 118);
      }
      if (this.mountHoles) {
        var self = this;
        ['tl', 'tr', 'bl', 'br'].forEach(function (key) {
          var el = self.mountHoles[key];
          var pos = geo.holes[key];
          if (el && pos) {
            el.setAttribute('cx', pos.cx);
            el.setAttribute('cy', pos.cy);
          }
        });
      }
      this.geometry.safeZoneX = geo.safeZoneX;
      this.geometry.safeZoneY = geo.safeZoneY;
      this.geometry.safeZoneWidth = geo.safeZoneWidth;
      this.geometry.safeZoneHeight = geo.safeZoneHeight;
    }

    /* ---------------------------------------------------------------- */
    /* State                                                            */
    /* ---------------------------------------------------------------- */
    _getDefaultState() {
      return {
        configurationId: this.generateConfigurationId(),
        contentMode: 'text', /* 'text' | 'company' | 'logo' */
        text: this.defaultText,
        companyName: '',
        logoFileName: '',
        fontFamily: this.defaultFont,
        fontWeight: 700,
        textColor: this.defaultColor,
        letterSpacing: 0,
        material: this.defaultMaterial,
        leftSlot: null, /* { type:'preset', key } | { type:'social', platform, handle } */
        rightSlot: null
      };
    }

    getState() {
      return JSON.parse(JSON.stringify(this.state));
    }

    restoreState(state) {
      if (!state || typeof state !== 'object') return;
      this.state = Object.assign(this._getDefaultState(), state);
      this._textColorTouched = true;
      this.render();
    }

    reset() {
      this._textColorTouched = false;
      this._clearLogoObjectUrl();
      if (this.uploadInput) this.uploadInput.value = '';
      this._setUploadError('');
      this.state = this._getDefaultState();
      this._syncControlsFromState();
      this.render();
    }

    generateConfigurationId() {
      return 'cfg_' + Date.now().toString(36) + '_' + Math.random().toString(36).slice(2, 8);
    }

    /* ---------------------------------------------------------------- */
    /* Public setters                                                   */
    /* ---------------------------------------------------------------- */
    setText(value) {
      var clean = sanitizeVisibleText(String(value == null ? '' : value));
      if (clean.length > this.maxChars) clean = clean.slice(0, this.maxChars);
      this.state.contentMode = 'text';
      this.state.text = clean;
      this._debouncedRenderText();
      this._updateCounter();
      this._updateHiddenInputs();
      this._updateAddToCartAvailability();
    }

    setCompanyName(value) {
      var clean = sanitizeVisibleText(String(value == null ? '' : value));
      if (clean.length > this.maxChars) clean = clean.slice(0, this.maxChars);
      this.state.contentMode = 'company';
      this.state.companyName = clean;
      this._debouncedRenderText();
      this._updateHiddenInputs();
      this._updateAddToCartAvailability();
    }

    setFont(fontFamily) {
      this.state.fontFamily = fontFamily;
      this._renderText();
      this._updateHiddenInputs();
    }

    setFontWeight(weight) {
      this.state.fontWeight = parseInt(weight, 10) || 400;
      this._renderText();
      this._updateHiddenInputs();
    }

    setTextColor(color) {
      this._textColorTouched = true;
      this.state.textColor = color;
      this.textEl.setAttribute('fill', color);
      this._updateHiddenInputs();
    }

    setLetterSpacing(value) {
      this.state.letterSpacing = parseFloat(value) || 0;
      this._renderText();
      this._updateHiddenInputs();
    }

    _initVariantLink() {
      if (this._variantLinkDone) return;
      this._variantLinkDone = true;
      var form = this.formId ? document.getElementById(this.formId) : null;
      var select = form ? form.querySelector('[data-configurator-variant]') : document.querySelector('[data-configurator-variant][form="' + this.formId + '"]');
      if (!select) return;
      var self = this;
      this.variantSelect = select;
      this.materialVariant = {};
      var options = Array.prototype.slice.call(select.options);
      var covered = {};
      (this.materialButtons || []).forEach(function (btn) {
        var key = btn.getAttribute('data-configurator-material');
        var want = MATERIAL_VARIANT_MATCH[key];
        var opt = want && options.filter(function (o) { return (o.getAttribute('data-title') || '').trim() === want && !o.disabled; })[0];
        if (opt) { self.materialVariant[key] = opt.value; covered[opt.value] = key; }
        else { btn.hidden = true; btn.style.display = 'none'; }
      });
      this.variantMaterial = covered;
      if (options.every(function (o) { return covered[o.value]; })) {
        var group = select.closest('.plate-holder-configurator__group');
        if (group) { group.hidden = true; group.style.display = 'none'; }
      }
      select.addEventListener('change', function () {
        var key = self.variantMaterial[select.value];
        if (key && key !== self.state.material) { self.setMaterial(key); self._syncControlsFromState(); }
        self._updateHiddenInputs();
      });
      if (!this.materialVariant[this.state.material]) {
        var first = covered[select.value] || Object.keys(this.materialVariant)[0];
        if (first) this.state.material = first;
      }
    }

    _syncVariantFromMaterial() {
      if (!this.variantSelect) return;
      var vid = this.materialVariant[this.state.material];
      if (vid && this.variantSelect.value !== vid) {
        this.variantSelect.value = vid;
        this.variantSelect.dispatchEvent(new Event('change', { bubbles: true }));
      }
    }

    _selectedVariantTitle() {
      if (!this.variantSelect) return null;
      var o = this.variantSelect.options[this.variantSelect.selectedIndex];
      return o ? (o.getAttribute('data-title') || '').trim() : null;
    }

    setMaterial(materialKey) {
      var material = MATERIALS[materialKey];
      if (!material) return;
      if (this.materialVariant && !this.materialVariant[materialKey]) return;
      this.state.material = materialKey;
      this.materialFillEl.setAttribute('fill', 'url(#' + material.fill + '-' + this.instanceId + ')');
      this.materialPatternEl.setAttribute('fill', material.pattern ? 'url(#' + material.pattern + '-' + this.instanceId + ')' : 'transparent');
      this.reflectionEl.style.opacity = material.reflectionOpacity;
      if (!this._textColorTouched) {
        this.state.textColor = material.textColor;
        this.textEl.setAttribute('fill', material.textColor);
      }
      this._syncVariantFromMaterial();
      this._updateHiddenInputs();
    }

    setLeftLogo(logoKey) {
      this.state.leftSlot = logoKey ? { type: 'preset', key: logoKey } : null;
      this._renderLogoSlot('left');
      this._renderText();
      this._updateHiddenInputs();
    }

    setRightLogo(logoKey) {
      this.state.rightSlot = logoKey ? { type: 'preset', key: logoKey } : null;
      this._renderLogoSlot('right');
      this._renderText();
      this._updateHiddenInputs();
    }

    setLeftSocial(platform, handle) {
      this.state.leftSlot = platform ? { type: 'social', platform: platform, handle: sanitizeVisibleText(handle || '') } : null;
      this._renderLogoSlot('left');
      this._renderText();
      this._updateHiddenInputs();
    }

    setRightSocial(platform, handle) {
      this.state.rightSlot = platform ? { type: 'social', platform: platform, handle: sanitizeVisibleText(handle || '') } : null;
      this._renderLogoSlot('right');
      this._renderText();
      this._updateHiddenInputs();
    }

    setLogoFile(file) {
      if (!file) return;
      if (ALLOWED_LOGO_TYPES.indexOf(file.type) === -1) {
        this._setUploadError('Kies een PNG-, JPG- of WebP-bestand.');
        if (this.uploadInput) this.uploadInput.value = '';
        return;
      }
      if (file.size > MAX_LOGO_FILE_BYTES) {
        this._setUploadError('Bestand is te groot (max 5 MB).');
        if (this.uploadInput) this.uploadInput.value = '';
        return;
      }
      this._setUploadError('');
      this._clearLogoObjectUrl();
      this._logoObjectUrl = URL.createObjectURL(file);
      this.state.contentMode = 'logo';
      this.state.logoFileName = file.name;
      this._toggleContentModeInputs();
      this._renderText();
      this._updateCounter();
      this._updateHiddenInputs();
      this._updateAddToCartAvailability();
      this._syncControlsFromState();
    }

    clearLogoFile() {
      this._clearLogoObjectUrl();
      this.state.logoFileName = '';
      this.state.contentMode = 'text';
      if (this.uploadInput) this.uploadInput.value = '';
      this._setUploadError('');
      this._toggleContentModeInputs();
      this._renderText();
      this._updateCounter();
      this._updateHiddenInputs();
      this._updateAddToCartAvailability();
      this._syncControlsFromState();
    }

    _clearLogoObjectUrl() {
      if (this._logoObjectUrl) {
        URL.revokeObjectURL(this._logoObjectUrl);
        this._logoObjectUrl = null;
      }
    }

    _setUploadError(message) {
      if (this.uploadErrorEl) this.uploadErrorEl.textContent = message || '';
    }

    removeLeftLogo() {
      this.state.leftSlot = null;
      this._renderLogoSlot('left');
      this._renderText();
      this._updateHiddenInputs();
    }

    removeRightLogo() {
      this.state.rightSlot = null;
      this._renderLogoSlot('right');
      this._renderText();
      this._updateHiddenInputs();
    }

    /* ---------------------------------------------------------------- */
    /* Export                                                           */
    /* ---------------------------------------------------------------- */
    exportSVG() {
      return new XMLSerializer().serializeToString(this.svg);
    }

    exportPNG(scale) {
      var self = this;
      scale = scale || 3;
      return new Promise(function (resolve, reject) {
        try {
          var svgString = self.exportSVG();
          var svgBlob = new Blob([svgString], { type: 'image/svg+xml;charset=utf-8' });
          var url = URL.createObjectURL(svgBlob);
          var img = new Image();
          img.onload = function () {
            var canvas = document.createElement('canvas');
            canvas.width = self.geometry.width * scale;
            canvas.height = self.geometry.height * scale;
            var ctx = canvas.getContext('2d');
            ctx.drawImage(img, 0, 0, canvas.width, canvas.height);
            URL.revokeObjectURL(url);
            canvas.toBlob(function (blob) {
              if (blob) resolve(blob);
              else reject(new Error('PNG-export mislukt.'));
            }, 'image/png');
          };
          img.onerror = function () {
            URL.revokeObjectURL(url);
            reject(new Error('PNG-export mislukt: SVG kon niet worden geladen.'));
          };
          img.src = url;
        } catch (err) {
          reject(err);
        }
      });
    }

    /* ---------------------------------------------------------------- */
    /* Rendering                                                        */
    /* ---------------------------------------------------------------- */
    render() {
      this._initVariantLink();
      this.setMaterial(this.state.material);
      this._renderLogoSlot('left');
      this._renderLogoSlot('right');
      this._renderText();
      this._syncControlsFromState();
      this._updateCounter();
      this._updateHiddenInputs();
      this._updateAddToCartAvailability();
    }

    _activeDisplayText() {
      if (this.state.contentMode === 'logo') return '';
      var raw = this.state.contentMode === 'company' ? this.state.companyName : this.state.text;
      /* leading/trailing spaces are stripped for the rendered/stored value; the
         input field itself keeps the user's raw value so typing isn't disrupted */
      return raw.replace(/^\s+|\s+$/g, '');
    }

    _slotReservedWidth(slot) {
      if (!slot) return 0;
      var handleWidth = 0;
      if (slot.type === 'social' && slot.handle) {
        handleWidth = typeof slot._measuredHandleWidth === 'number' ? slot._measuredHandleWidth : this._estimateSocialHandleWidth(slot.handle);
      }
      return LOGO_SLOT_SIZE + LOGO_SLOT_GAP + handleWidth;
    }

    _estimateSocialHandleWidth(handle) {
      /* rough estimate before layout; refined by actual measurement in _renderLogoSlot */
      return handle.length * 6;
    }

    _renderText() {
      if (this.state.contentMode === 'logo') {
        this.textEl.textContent = '';
        this._updateSilhouette(Math.ceil(this.maxChars * 0.6));
        this._renderLogoImage();
        return;
      }
      if (this.logoImageEl) this.logoImageEl.setAttribute('visibility', 'hidden');

      var text = this._activeDisplayText();
      var el = this.textEl;
      var charCount = text ? text.length : 0;

      /* Grow bottom tab with letter count first — letters follow the vak. */
      this._updateSilhouette(Math.max(charCount, 1));

      el.textContent = text && text.length ? text : '';
      el.setAttribute('font-family', this.state.fontFamily);
      el.setAttribute('font-weight', this.state.fontWeight);
      el.setAttribute('letter-spacing', this.state.letterSpacing);
      el.setAttribute('fill', this.state.textColor);

      if (!text) {
        el.setAttribute('font-size', this.maxFontSize);
        this._positionText(0, 0);
        this._setTextError('');
        this._updateAddToCartAvailability();
        return;
      }

      var leftReserved = this._slotReservedWidth(this.state.leftSlot);
      var rightReserved = this._slotReservedWidth(this.state.rightSlot);
      var availableWidth = Math.max(this.geometry.safeZoneWidth - leftReserved - rightReserved, 4);
      var availableHeight = Math.max(this.geometry.safeZoneHeight, 20);

      /* Font tracks the tab: fill most of the tab height (readable), then
         only trim if the measured width would leave the black area.
         No aggressive per-letter shrink — wider tab keeps letters large. */
      var size = Math.min(this.maxFontSize, availableHeight * 0.78);
      /* Mild ease-down for long strings so 10 chars look balanced, not tiny */
      var mild = 1 - (Math.max(0, charCount - 1) / Math.max(1, this.maxChars - 1)) * 0.18;
      size = Math.max(this.minFontSize, size * mild);

      el.setAttribute('font-size', size);
      var measured = 0;
      try {
        measured = el.getComputedTextLength();
      } catch (e) {
        measured = text.length * size * 0.55;
      }

      /* If still too wide: widen tab further to match the text, then re-measure.
         Prefer growing the vak over shrinking the letters. */
      if (measured > availableWidth) {
        var needHalf = (measured + 28 + leftReserved + rightReserved) / 2;
        var geo = this._buildSilhouette(charCount);
        var cx = 280;
        var maxHalf = (542 - 18) / 2 - 36;
        var boostedHalf = Math.min(maxHalf, Math.max((geo.tabShade.width / 2), needHalf));
        /* Rebuild with a synthetic char flare that yields ~boostedHalf */
        var tNeeded = Math.max(0, Math.min(1, (boostedHalf - 70) / 175));
        var syntheticCount = Math.round(1 + tNeeded * (this.maxChars - 1));
        this._updateSilhouette(Math.max(charCount, syntheticCount));
        availableWidth = Math.max(this.geometry.safeZoneWidth - leftReserved - rightReserved, 4);
        availableHeight = Math.max(this.geometry.safeZoneHeight, 20);
        size = Math.min(this.maxFontSize, availableHeight * 0.78);
        size = Math.max(this.minFontSize, size * mild);
        el.setAttribute('font-size', size);
        try {
          measured = el.getComputedTextLength();
        } catch (e) {
          measured = text.length * size * 0.55;
        }
      }

      /* Last resort: nudge font down in small steps so glyphs stay on-plate */
      var floor = Math.max(this.minFontSize, availableHeight * 0.50);
      while (measured > availableWidth && size > floor) {
        size -= 0.5;
        el.setAttribute('font-size', size);
        try {
          measured = el.getComputedTextLength();
        } catch (e) {
          measured = text.length * size * 0.55;
        }
      }

      var fits = measured <= availableWidth;
      this._setTextError(fits ? '' : this.warningText);
      this._positionText(leftReserved, rightReserved);
      this._updateAddToCartAvailability();
    }

    _renderLogoImage() {
      if (!this.logoImageEl) return;
      if (!this._logoObjectUrl) {
        this.logoImageEl.setAttribute('visibility', 'hidden');
        this._setTextError('');
        this._updateAddToCartAvailability();
        return;
      }
      var leftReserved = this._slotReservedWidth(this.state.leftSlot);
      var rightReserved = this._slotReservedWidth(this.state.rightSlot);
      var boxWidth = Math.max(this.geometry.safeZoneWidth - leftReserved - rightReserved, 4);
      var boxX = this.geometry.safeZoneX + leftReserved;

      this.logoImageEl.setAttributeNS('http://www.w3.org/1999/xlink', 'href', this._logoObjectUrl);
      this.logoImageEl.setAttribute('href', this._logoObjectUrl);
      this.logoImageEl.setAttribute('x', boxX);
      this.logoImageEl.setAttribute('y', this.geometry.safeZoneY);
      this.logoImageEl.setAttribute('width', boxWidth);
      this.logoImageEl.setAttribute('height', this.geometry.safeZoneHeight);
      this.logoImageEl.setAttribute('visibility', 'visible');
      this._setTextError('');
      this._updateAddToCartAvailability();
    }

    _positionText(leftReserved, rightReserved) {
      var zoneLeft = this.geometry.safeZoneX + leftReserved;
      var zoneRight = this.geometry.safeZoneX + this.geometry.safeZoneWidth - rightReserved;
      var centerX = (zoneLeft + zoneRight) / 2;
      var centerY = this.geometry.safeZoneY + this.geometry.safeZoneHeight / 2;
      this.textEl.setAttribute('x', centerX);
      this.textEl.setAttribute('y', centerY);
    }

    _renderLogoSlot(side) {
      var group = side === 'left' ? this.leftLogoGroup : this.rightLogoGroup;
      var slot = side === 'left' ? this.state.leftSlot : this.state.rightSlot;
      while (group.firstChild) group.removeChild(group.firstChild);

      if (!slot) {
        group.setAttribute('visibility', 'hidden');
        return;
      }
      group.setAttribute('visibility', 'visible');

      var symbolId = null;
      if (slot.type === 'preset' && PRESET_LOGOS[slot.key]) {
        symbolId = PRESET_LOGOS[slot.key].symbol;
      } else if (slot.type === 'social' && SOCIAL_PLATFORMS[slot.platform]) {
        symbolId = SOCIAL_PLATFORMS[slot.platform].symbol;
      }
      if (!symbolId) {
        group.setAttribute('visibility', 'hidden');
        return;
      }

      var use = document.createElementNS('http://www.w3.org/2000/svg', 'use');
      use.setAttributeNS('http://www.w3.org/1999/xlink', 'href', '#' + symbolId + '-' + this.instanceId);
      use.setAttribute('href', '#' + symbolId + '-' + this.instanceId);
      var half = LOGO_SLOT_SIZE / 2;
      use.setAttribute('x', -half);
      use.setAttribute('y', -half);
      use.setAttribute('width', LOGO_SLOT_SIZE);
      use.setAttribute('height', LOGO_SLOT_SIZE);
      use.setAttribute('color', this.state.textColor);
      group.appendChild(use);

      var zoneCenterX = this.geometry.safeZoneX + (side === 'left' ? half + 2 : this.geometry.safeZoneWidth - half - 2);
      var centerY = this.geometry.safeZoneY + this.geometry.safeZoneHeight / 2;

      if (slot.type === 'social' && slot.handle) {
        var handleText = document.createElementNS('http://www.w3.org/2000/svg', 'text');
        handleText.setAttribute('data-social-handle', 'true');
        handleText.setAttribute('font-family', this.state.fontFamily);
        handleText.setAttribute('font-size', Math.min(this.maxFontSize, 9));
        handleText.setAttribute('fill', this.state.textColor);
        handleText.setAttribute('dominant-baseline', 'central');
        handleText.textContent = slot.handle;
        if (side === 'left') {
          handleText.setAttribute('x', LOGO_SLOT_SIZE / 2 + 3);
          handleText.setAttribute('y', 0);
          handleText.setAttribute('text-anchor', 'start');
        } else {
          handleText.setAttribute('x', -(LOGO_SLOT_SIZE / 2 + 3));
          handleText.setAttribute('y', 0);
          handleText.setAttribute('text-anchor', 'end');
        }
        group.appendChild(handleText);
        try {
          slot._measuredHandleWidth = handleText.getComputedTextLength();
        } catch (e) {
          /* measurement unavailable, keep the rough estimate */
        }
      }

      group.setAttribute('transform', 'translate(' + zoneCenterX + ',' + centerY + ')');
    }

    /* ---------------------------------------------------------------- */
    /* Hidden Shopify line item property inputs                        */
    /* ---------------------------------------------------------------- */
    _updateHiddenInputs() {
      var self = this;
      var map = {
        text: (this._activeDisplayText() || '').trim(),
        fontFamily: this.state.fontFamily,
        fontWeight: this.state.fontWeight,
        textColor: this.state.textColor,
        letterSpacing: this.state.letterSpacing,
        material: this._selectedVariantTitle() || (MATERIALS[this.state.material] || {}).label || this.state.material,
        leftLogo: this._describeSlot(this.state.leftSlot),
        rightLogo: this._describeSlot(this.state.rightSlot),
        configurationId: this.state.configurationId,
        configurationJson: JSON.stringify(Object.assign({}, this.state, { text: (this.state.text || '').trim(), companyName: (this.state.companyName || '').trim() }))
      };
      Object.keys(map).forEach(function (key) {
        var input = self.querySelector('[data-prop="' + key + '"]');
        if (input) input.value = map[key] == null ? '' : String(map[key]);
      });
    }

    _describeSlot(slot) {
      if (!slot) return 'Geen';
      if (slot.type === 'preset') return (PRESET_LOGOS[slot.key] || {}).label || slot.key;
      if (slot.type === 'social') {
        var platform = (SOCIAL_PLATFORMS[slot.platform] || {}).label || slot.platform;
        return slot.handle ? platform + ' (' + slot.handle + ')' : platform;
      }
      return 'Geen';
    }

    /* ---------------------------------------------------------------- */
    /* Validation / UI wiring                                           */
    /* ---------------------------------------------------------------- */
    _setTextError(message) {
      if (!this.errorEl) return;
      this.errorEl.textContent = message || '';
    }

    _updateCounter() {
      if (!this.counterEl) return;
      var length = this.state.contentMode === 'company' ? this.state.companyName.length : this.state.text.length;
      this.counterEl.textContent = length + ' / ' + this.maxChars;
    }

    _isValid() {
      if (this.state.contentMode === 'logo') return !!this._logoObjectUrl;
      var text = this._activeDisplayText();
      return !!(text && text.trim().length) && !(this.errorEl && this.errorEl.textContent);
    }

    _updateAddToCartAvailability() {
      var form = this.formId ? document.getElementById(this.formId) : null;
      if (!form) return;
      var submitBtn = form.querySelector('[type="submit"]');
      if (!submitBtn) return;
      var valid = this._isValid();
      if (!valid) {
        submitBtn.setAttribute('aria-disabled', 'true');
        submitBtn.disabled = true;
        submitBtn.classList.add('plate-holder-configurator-blocking');
      } else if (submitBtn.classList.contains('plate-holder-configurator-blocking')) {
        submitBtn.removeAttribute('aria-disabled');
        submitBtn.disabled = false;
        submitBtn.classList.remove('plate-holder-configurator-blocking');
      }
    }

    _cacheControls() {
      this.textInput = this.querySelector('[data-configurator-text-input]');
      this.companyInput = this.querySelector('[data-configurator-company-input]');
      this.counterEl = this.querySelector('[data-configurator-counter]');
      this.errorEl = this.querySelector('[data-configurator-error]');
      this.fontButtons = Array.prototype.slice.call(this.querySelectorAll('[data-configurator-font]'));
      this.weightSelect = this.querySelector('[data-configurator-weight]');
      this.colorButtons = Array.prototype.slice.call(this.querySelectorAll('[data-configurator-color]'));
      this.spacingInput = this.querySelector('[data-configurator-spacing]');
      this.materialButtons = Array.prototype.slice.call(this.querySelectorAll('[data-configurator-material]'));
      this.leftLogoButtons = Array.prototype.slice.call(this.querySelectorAll('[data-configurator-left-logo]'));
      this.rightLogoButtons = Array.prototype.slice.call(this.querySelectorAll('[data-configurator-right-logo]'));
      this.leftSocialButtons = Array.prototype.slice.call(this.querySelectorAll('[data-configurator-left-social]'));
      this.rightSocialButtons = Array.prototype.slice.call(this.querySelectorAll('[data-configurator-right-social]'));
      this.contentModeButtons = Array.prototype.slice.call(this.querySelectorAll('[data-configurator-content-mode]'));
      this.resetBtn = this.querySelector('[data-configurator-reset]');
      this.uploadGroup = this.querySelector('[data-configurator-upload-group]');
      this.uploadInput = this.querySelector('[data-configurator-upload-input]');
      this.uploadTrigger = this.querySelector('[data-configurator-upload-trigger]');
      this.uploadFilenameEl = this.querySelector('[data-configurator-upload-filename]');
      this.uploadRemoveBtn = this.querySelector('[data-configurator-upload-remove]');
      this.uploadErrorEl = this.querySelector('[data-configurator-upload-error]');
    }

    _bindControls() {
      var self = this;

      if (this.textInput) {
        this.textInput.addEventListener('input', function () {
          self.setText(this.value);
        });
      }
      if (this.companyInput) {
        this.companyInput.addEventListener('input', function () {
          self.setCompanyName(this.value);
        });
      }
      this.contentModeButtons.forEach(function (btn) {
        btn.addEventListener('click', function () {
          var mode = btn.getAttribute('data-configurator-content-mode');
          self.state.contentMode = mode;
          self._toggleContentModeInputs();
          self._renderText();
          self._updateCounter();
          self._updateHiddenInputs();
          self._updateAddToCartAvailability();
          self._syncControlsFromState();
          var activeInput = mode === 'company' ? self.companyInput : (mode === 'logo' ? self.uploadTrigger : self.textInput);
          if (activeInput) activeInput.focus();
        });
      });

      if (this.uploadInput) {
        this.uploadInput.addEventListener('change', function () {
          var file = this.files && this.files[0];
          if (file) self.setLogoFile(file);
        });
      }

      if (this.uploadRemoveBtn) {
        this.uploadRemoveBtn.addEventListener('click', function () {
          self.clearLogoFile();
          self._syncControlsFromState();
          if (self.textInput) self.textInput.focus();
        });
      }

      this.fontButtons.forEach(function (btn) {
        btn.addEventListener('click', function () {
          self.setFont(btn.getAttribute('data-configurator-font'));
          self._syncControlsFromState();
        });
      });

      if (this.weightSelect) {
        this.weightSelect.addEventListener('change', function () {
          self.setFontWeight(this.value);
        });
      }

      this.colorButtons.forEach(function (btn) {
        btn.addEventListener('click', function () {
          self.setTextColor(btn.getAttribute('data-configurator-color'));
          self._syncControlsFromState();
        });
      });

      if (this.spacingInput) {
        this.spacingInput.addEventListener('input', function () {
          self.setLetterSpacing(this.value);
        });
      }

      this.materialButtons.forEach(function (btn) {
        btn.addEventListener('click', function () {
          self.setMaterial(btn.getAttribute('data-configurator-material'));
          self._syncControlsFromState();
        });
      });

      this.leftLogoButtons.forEach(function (btn) {
        btn.addEventListener('click', function () {
          var key = btn.getAttribute('data-configurator-left-logo');
          self.setLeftLogo(key === 'none' ? null : key);
          self._syncControlsFromState();
        });
      });

      this.rightLogoButtons.forEach(function (btn) {
        btn.addEventListener('click', function () {
          var key = btn.getAttribute('data-configurator-right-logo');
          self.setRightLogo(key === 'none' ? null : key);
          self._syncControlsFromState();
        });
      });

      this.leftSocialButtons.forEach(function (btn) {
        btn.addEventListener('click', function () {
          self.setLeftSocial(btn.getAttribute('data-configurator-left-social'));
          self._syncControlsFromState();
        });
      });

      this.rightSocialButtons.forEach(function (btn) {
        btn.addEventListener('click', function () {
          self.setRightSocial(btn.getAttribute('data-configurator-right-social'));
          self._syncControlsFromState();
        });
      });

      if (this.resetBtn) {
        this.resetBtn.addEventListener('click', function () {
          self.reset();
        });
      }

      var form = this.formId ? document.getElementById(this.formId) : null;
      if (form) {
        form.addEventListener('submit', function () {
          self._updateHiddenInputs();
        });
      }
    }

    _toggleContentModeInputs() {
      var mode = this.state.contentMode;
      if (this.textInput) this.textInput.hidden = mode !== 'text';
      if (this.companyInput) this.companyInput.hidden = mode !== 'company';
      if (this.uploadGroup) this.uploadGroup.hidden = mode !== 'logo';
      if (this.counterEl) this.counterEl.hidden = mode === 'logo';
    }

    _syncControlsFromState() {
      var self = this;
      this._toggleContentModeInputs();
      if (this.textInput) this.textInput.value = this.state.text;
      if (this.companyInput) this.companyInput.value = this.state.companyName;
      this.contentModeButtons.forEach(function (btn) {
        var isActive = btn.getAttribute('data-configurator-content-mode') === self.state.contentMode;
        btn.classList.toggle('is-selected', isActive);
        btn.setAttribute('aria-pressed', isActive ? 'true' : 'false');
      });
      if (this.uploadTrigger) this.uploadTrigger.hidden = !!this._logoObjectUrl;
      if (this.uploadFilenameEl) {
        this.uploadFilenameEl.hidden = !this._logoObjectUrl;
        var nameSpan = this.uploadFilenameEl.querySelector('[data-configurator-upload-filename-text]');
        if (nameSpan) nameSpan.textContent = this.state.logoFileName || '';
      }
      this.fontButtons.forEach(function (btn) {
        var isActive = btn.getAttribute('data-configurator-font') === self.state.fontFamily;
        btn.classList.toggle('is-selected', isActive);
      });
      if (this.weightSelect) this.weightSelect.value = String(this.state.fontWeight);
      this.colorButtons.forEach(function (btn) {
        var isActive = btn.getAttribute('data-configurator-color') === self.state.textColor;
        btn.classList.toggle('is-selected', isActive);
        btn.setAttribute('aria-pressed', isActive ? 'true' : 'false');
      });
      if (this.spacingInput) this.spacingInput.value = String(this.state.letterSpacing);
      this.materialButtons.forEach(function (btn) {
        var isActive = btn.getAttribute('data-configurator-material') === self.state.material;
        btn.classList.toggle('is-selected', isActive);
        btn.setAttribute('aria-pressed', isActive ? 'true' : 'false');
      });
      this.leftLogoButtons.forEach(function (btn) {
        var key = btn.getAttribute('data-configurator-left-logo');
        var isActive = (key === 'none' && !self.state.leftSlot) || (self.state.leftSlot && self.state.leftSlot.key === key);
        btn.classList.toggle('is-selected', isActive);
      });
      this.rightLogoButtons.forEach(function (btn) {
        var key = btn.getAttribute('data-configurator-right-logo');
        var isActive = (key === 'none' && !self.state.rightSlot) || (self.state.rightSlot && self.state.rightSlot.key === key);
        btn.classList.toggle('is-selected', isActive);
      });
      this.leftSocialButtons.forEach(function (btn) {
        var platform = btn.getAttribute('data-configurator-left-social');
        var isActive = !!(self.state.leftSlot && self.state.leftSlot.type === 'social' && self.state.leftSlot.platform === platform);
        btn.classList.toggle('is-selected', isActive);
      });
      this.rightSocialButtons.forEach(function (btn) {
        var platform = btn.getAttribute('data-configurator-right-social');
        var isActive = !!(self.state.rightSlot && self.state.rightSlot.type === 'social' && self.state.rightSlot.platform === platform);
        btn.classList.toggle('is-selected', isActive);
      });
    }
  }

  customElements.define('plate-holder-configurator', PlateHolderConfiguratorElement);

  /* expose material/social/logo dictionaries for theme-editor-driven scripts if ever needed */
  window.PlateHolderConfiguratorData = {
    MATERIALS: MATERIALS,
    SOCIAL_PLATFORMS: SOCIAL_PLATFORMS,
    PRESET_LOGOS: PRESET_LOGOS
  };
})();
